import { Spinner } from 'components/loaders'
import { FC, ReactNode, memo, useLayoutEffect, useRef, useState } from 'react'
import { twMerge } from '@lib/tailwind-merge'
import { ScrollableTableBottom } from './ScrollableTableBottom'

interface BaseProps {
  body: ReactNode
  footer?: ReactNode
  extraContent?: ReactNode
  header?: ReactNode
  rootClassName?: string
  tableClassName?: string
}

type Props =
  | BaseProps
  | (BaseProps & {
      isLoading: boolean
      numOfColumns: number
    })
  | (BaseProps & {
      hasNextPage: boolean
      isFetchingNextPage: boolean
      isLoading: boolean
      numOfColumns: number
      onNextPage: () => void
    })

export const ScrollableTable: FC<Props> = memo(function ScrollableTable({
  body,
  footer,
  extraContent,
  header,
  rootClassName,
  tableClassName,
  ...props
}) {
  const ref = useRef<HTMLTableSectionElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useLayoutEffect(() => {
    setHeaderHeight(ref.current?.clientHeight ?? 0)
  }, [])

  return (
    <div className={twMerge('relative z-0 flex-grow mx-8', rootClassName)}>
      <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col overflow-auto">
        <table
          className={twMerge(
            'min-w-full text-sm border-separate border-spacing-0 border-neutral-300',
            tableClassName,
          )}
        >
          {header && (
            <thead
              className="sticky z-20 shadow first:[&_th]:border-l first:[&_th]:border-neutral-300"
              style={{ insetBlockStart: 0 }}
              ref={ref}
            >
              {header}
            </thead>
          )}
          <tbody className="first:[&_td]:border-l first:[&_td]:border-neutral-300">
            {'isLoading' in props && props.isLoading ? (
              <tr>
                <td
                  colSpan={props.numOfColumns}
                  className="p-6 text-center border-b border-r border-neutral-300"
                >
                  <Spinner className="text-2xl" />
                </td>
              </tr>
            ) : (
              body
            )}
            {'onNextPage' in props && (
              <ScrollableTableBottom
                hasNextPage={props.hasNextPage}
                isFetchingNextPage={props.isFetchingNextPage}
                onNextPage={props.onNextPage}
                numOfColumns={props.numOfColumns}
              />
            )}
          </tbody>
          {footer && (
            <tfoot
              className="sticky first:[&_td]:border-l first:[&_td]:border-neutral-300"
              style={{ insetBlockEnd: 0 }}
            >
              {footer}
            </tfoot>
          )}
        </table>
      </div>
      {extraContent && (
        <div
          className="relative z-10 flex flex-col border border-neutral-300"
          style={{ marginTop: headerHeight }}
        >
          {extraContent}
        </div>
      )}
    </div>
  )
})
